import { useTranslation } from 'react-i18next'
import { CircleCheck, CircleDashed, CircleMinus } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Button } from '@/src/renderer/components/ui/Button'
import { SegmentedControl } from '@/src/renderer/components/ui/SegmentedControl'
import { ProjectIcon } from '@/src/renderer/components/common/ProjectIcon'
import { TODOIST_PROJECT_ICON } from '@/src/renderer/lib/import/todoist/colors'
import type { LifeSpace } from '@/src/renderer/lib/db/models'
import type { ImportPreview as Preview, PreviewLine } from '@/src/renderer/lib/import/todoist/preview'
import { lifeSpaceFor, type LifeSpaceChoices } from '@/src/renderer/lib/import/todoist/lifeSpaces'
import './ImportPreview.scss'

interface ImportPreviewProps {
  preview: Preview
  /** Der Verlauf lädt noch — die Zeile „erledigt“ fehlt bis dahin. */
  completedLoading?: boolean
  /** Gewählter Lebensbereich je Projekt (T06); fehlt eine Wahl, gilt Persönlich. */
  lifeSpaces?: LifeSpaceChoices
  /** Ohne Handler bleibt die Projektliste nur zum Lesen. */
  onLifeSpaceChange?: (projectId: string, value: LifeSpace) => void
  /** „Alle auf …“ über der Liste. */
  onAllLifeSpaces?: (value: LifeSpace) => void
}

const LIFE_SPACES: LifeSpace[] = ['personal', 'work', 'home']

const BLOCKS: { id: 'arrives' | 'changed' | 'missing'; icon: LucideIcon }[] = [
  { id: 'arrives', icon: CircleCheck },
  { id: 'changed', icon: CircleDashed },
  { id: 'missing', icon: CircleMinus },
]

/**
 * **Vorschau des Todoist-Imports** (T05, #170): was ankommt, was verändert
 * ankommt, was nicht — mit Zahlen, bevor etwas geschrieben wird. Darunter die
 * Projekte, je Zeile mit der Wahl des Lebensbereichs (T06).
 *
 * Rechnet nichts selbst: die Zahlen kommen fertig aus `buildImportPreview`.
 *
 * @example
 * <ImportPreview preview={buildImportPreview(snapshot)} completedLoading={false} />
 */
export function ImportPreview({
  preview,
  completedLoading = false,
  lifeSpaces = {},
  onLifeSpaceChange,
  onAllLifeSpaces,
}: ImportPreviewProps) {
  const { t } = useTranslation()
  const lifeSpaceOptions = LIFE_SPACES.map(value => ({ value, label: t(`lifeSpaces.${value}`) }))

  const text = (line: PreviewLine) => {
    const archived = line.values?.archived ?? 0
    const key = (line.key === 'projects' || line.key === 'sections') && archived > 0 ? `${line.key}WithArchived` : line.key
    return line.count === null
      ? t(`preferences.import.preview.line.${key}`)
      : t(`preferences.import.preview.line.${key}`, { count: line.count, ...line.values })
  }

  return (
    <div className="import-preview">
      {BLOCKS.map(({ id, icon: Icon }) => {
        const lines = preview[id]
        if (lines.length === 0) return null
        return (
          <section key={id} className={`import-preview__block import-preview__block--${id}`}>
            <h3 className="import-preview__heading">
              <Icon size={16} aria-hidden="true" />
              {t(`preferences.import.preview.${id}`)}
            </h3>
            <ul className="import-preview__lines">
              {lines.map(line => <li key={line.key}>{text(line)}</li>)}
              {id === 'arrives' && completedLoading && (
                <li className="import-preview__pending">{t('preferences.import.preview.completedLoading')}</li>
              )}
            </ul>
          </section>
        )
      })}

      {preview.projects.length > 0 && (
        <section className="import-preview__block">
          <h3 className="import-preview__heading">{t('preferences.import.preview.projectsTitle')}</h3>
          {onLifeSpaceChange && (
            <>
              <p className="import-preview__hint">{t('preferences.import.preview.lifeSpaceHint')}</p>
              {onAllLifeSpaces && (
                <div className="import-preview__all">
                  <span>{t('preferences.import.preview.allTo')}</span>
                  {LIFE_SPACES.map(value => (
                    <Button key={value} size="sm" variant="secondary" onClick={() => onAllLifeSpaces(value)}>
                      {t(`lifeSpaces.${value}`)}
                    </Button>
                  ))}
                </div>
              )}
            </>
          )}
          <ul className="import-preview__projects">
            {preview.projects.map(project => (
              <li key={project.id} className="import-preview__project">
                {!project.inbox && <ProjectIcon icon={TODOIST_PROJECT_ICON} iconColor={project.iconColor} size={20} />}
                <span className="import-preview__project-name">{project.name}</span>
                {project.inbox && <span className="import-preview__badge">{t('preferences.import.preview.inboxTarget')}</span>}
                {project.archived && <span className="import-preview__badge">{t('preferences.import.preview.archived')}</span>}
                <span className="import-preview__project-count">
                  {t('preferences.import.preview.openTasks', { count: project.openTasks })}
                </span>
                {onLifeSpaceChange && (
                  <SegmentedControl
                    className="import-preview__life-space"
                    aria-label={t('preferences.import.preview.lifeSpaceFor', { name: project.name })}
                    value={lifeSpaceFor(lifeSpaces, project.id)}
                    onValueChange={value => onLifeSpaceChange(project.id, value)}
                    options={lifeSpaceOptions}
                  />
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
